import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Typography,
  useTheme,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import UndoIcon from "@mui/icons-material/Undo";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import {
  useGetBriefingByIdQuery,
  useUpdateBriefingMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { uploadBriefingImage } from "../../firebase/firebaseStorage";
import { components, operations } from "../../types/api";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import OrbLoader from "../../components/common/OrbLoader";
import SectionCard from "../../components/briefings/inline/SectionCard";
import FieldGroup, {
  useGridSpan,
} from "../../components/briefings/inline/FieldGroup";
import {
  FieldState,
  FieldTone,
  getFieldTone,
} from "../../components/briefings/inline/inlineTones";
import { useCopyToClipboard } from "../../components/common/CopyablePanel";
import NumberField from "../../components/common/NumberField";
import { longMonth } from "../../utils/briefings/dateRange";
import { useGoBack } from "../../hooks/useGoBack";

type UpdateBriefingRequest =
  operations["UpdateBriefing"]["requestBody"]["content"]["application/json"];

type BriefingType = components["schemas"]["_36_Enums.BriefingType"];

interface FormState {
  name: string;
  companyId: string;
  briefingType: BriefingType;
  campaignName: string;
  brandOrProduct: string;
  month: number;
  year: number;
  totalBudget: number;
  fee: number;
  trackingPixel: boolean;
  hasTags: boolean;
  notes: string;
  imageUrl?: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const defaultState: FormState = {
  name: "",
  companyId: "",
  briefingType: "PAID_MEDIA_MANAGED",
  campaignName: "",
  brandOrProduct: "",
  month: 1,
  year: 2025,
  totalBudget: 0,
  fee: 0,
  trackingPixel: false,
  hasTags: false,
  notes: "",
  imageUrl: "",
};

const BRIEFING_TYPES: BriefingType[] = [
  "PAID_MEDIA_MANAGED",
  "BUY_MEDIA_DIRECT_AND_OWN",
];

/** Formula: Month_Year_BriefingType_Brand_Campaign */
const buildBriefingName = (state: FormState) =>
  [
    state.month.toString().padStart(2, "0"),
    state.year,
    state.briefingType,
    state.brandOrProduct,
    state.campaignName,
  ]
    .filter(Boolean)
    .join("_");

/** The name is derived from the other fields, so it never counts as an edit. */
const EDITABLE_FIELDS: Array<keyof FormState> = [
  "companyId",
  "briefingType",
  "campaignName",
  "brandOrProduct",
  "month",
  "year",
  "totalBudget",
  "fee",
  "trackingPixel",
  "hasTags",
  "notes",
  "imageUrl",
];

const CompanyLogo: React.FC<{ name: string; logoUrl?: string }> = ({
  name,
  logoUrl,
}) => (
  <Avatar
    variant="rounded"
    src={logoUrl || undefined}
    alt=""
    sx={{
      width: 22,
      height: 22,
      fontSize: 12,
      fontWeight: 700,
      bgcolor: "action.selected",
      color: "text.primary",
      "& img": { objectFit: "contain" },
    }}
  >
    {name.charAt(0).toUpperCase()}
  </Avatar>
);

/** Pill input; a pending or calculated value tints the input itself. */
/** Compact pills: barely any vertical padding, a bit more on the sides. */
const PILL_PADDING = { py: "6px", px: 2 } as const;
/** Height of a compact pill, so radios/switches line up with the inputs. */
const PILL_HEIGHT = 38;

const pillSx = (tone: FieldTone, tinted: boolean, multiline = false) => ({
  "& .MuiOutlinedInput-input": multiline ? {} : PILL_PADDING,
  "& .MuiInputBase-multiline": { py: "8px", px: 2 },
  "& .MuiInputAdornment-root .MuiIconButton-root": { p: 0.25 },
  "& .MuiOutlinedInput-root": {
    borderRadius: multiline ? "16px" : 50,
    transition: "background-color .2s",
    ...(tinted && {
      bgcolor: tone.bg,
      "& fieldset, &:hover fieldset": { borderColor: tone.border },
    }),
  },
});

interface FieldProps {
  label: string;
  icon: React.ReactElement;
  state?: FieldState;
  auto?: boolean;
  autoHint?: string;
  onRevert?: () => void;
  error?: string;
  helper?: React.ReactNode;
  span?: number | "full";
  children: React.ReactNode;
}

/** Label row (icon, label, undo) over a bare input: no panel around it. */
const Field: React.FC<FieldProps> = ({
  label,
  icon,
  state = "idle",
  auto = false,
  autoHint,
  onRevert,
  error,
  helper,
  span = 1,
  children,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const gridColumn = useGridSpan(span);
  const tone = getFieldTone(theme, state, auto);

  return (
    <Box sx={{ gridColumn, minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minHeight: 26, mb: 0.5, px: 1, color: "text.secondary" }}
      >
        <Box
          sx={{
            display: "flex",
            color: state === "idle" && !auto ? "text.secondary" : tone.accent,
            "& svg": { fontSize: 16 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          noWrap
          title={label}
          sx={{ fontWeight: 600, letterSpacing: 0.2, minWidth: 0 }}
        >
          {label}
        </Typography>
        {auto && (
          <Tooltip
            arrow
            title={
              autoHint
                ? `${t("inlineEdit.autoValue", "Calculado automáticamente")}: ${autoHint}`
                : t("inlineEdit.autoValue", "Calculado automáticamente")
            }
          >
            <AutoAwesomeIcon
              sx={{ fontSize: 13, color: theme.palette.info.main }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {state === "dirty" && onRevert && (
          <Tooltip title={t("inlineEdit.revert", "Deshacer cambio")}>
            <IconButton
              size="small"
              onClick={onRevert}
              sx={{ p: 0.4, borderRadius: 1.5, color: tone.text }}
            >
              <UndoIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {children}
      {(error || helper) && (
        <Box
          sx={{
            mt: 0.5,
            px: 1,
            typography: "caption",
            color: error ? "error.main" : "text.secondary",
          }}
        >
          {error || helper}
        </Box>
      )}
    </Box>
  );
};

/** Pill text/select input whose tint follows the field state. */
const PillInput: React.FC<
  TextFieldProps & { state: FieldState; auto?: boolean }
> = ({ state, auto = false, multiline, sx, type, ...props }) => {
  const theme = useTheme();
  const tone = getFieldTone(theme, state, auto);
  // type="number" → NumberField: acepta coma o punto como decimal.
  const Input: React.ElementType = type === "number" ? NumberField : TextField;
  return (
    <Input
      fullWidth
      variant="outlined"
      multiline={multiline}
      {...(type === "number" ? {} : { type })}
      {...props}
      sx={[
        pillSx(tone, state === "dirty" || auto, multiline),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

/**
 * Borderless row for controls that aren't inputs (radios, switches), as tall
 * as a pill so it lines up with the inputs next to it.
 */
const ControlRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: PILL_HEIGHT,
      px: 0.5,
      display: "flex",
      alignItems: "center",
      "& .MuiRadio-root": { p: 0.5 },
      // MUI pulls FormControlLabel 11px left by default, which put the radio
      // against the field next to it. Instead line the radio up with the text
      // of the pill inputs (16px in).
      "& .MuiFormControlLabel-root": { ml: 0 },
      "& .MuiRadioGroup-root": { pl: 1 },
      "& .MuiSwitch-root": { my: -0.5 },
    }}
  >
    {children}
  </Box>
);

const EditBriefing: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const { id } = useParams<{ id: string }>();

  const canManageBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "ACCOUNT";

  const {
    data: companiesResponse,
    isLoading: isLoadingCompanies,
    error: companiesError,
  } = useGetCompaniesQuery(undefined, { skip: !canManageBriefings });

  const {
    data: briefingResponse,
    isFetching: isFetchingBriefing,
    error: briefingError,
  } = useGetBriefingByIdQuery(
    { id: id ?? "" },
    { skip: !canManageBriefings || !id }
  );

  const [updateBriefing, { isLoading: isUpdating }] =
    useUpdateBriefingMutation();

  // `base` is the saved briefing: what the form compares against and
  // goes back to on discard.
  const [base, setBase] = useState<FormState>(defaultState);
  const [formState, setFormState] = useState<FormState>(defaultState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const copy = useCopyToClipboard();

  useEffect(() => {
    if (briefingResponse?.data) {
      if (briefingResponse.data.status === "FINALIZED") {
        navigate(`/briefings/${id}`, { replace: true });
        return;
      }
      const briefing = briefingResponse.data;

      const loaded: FormState = {
        name: briefing.name,
        companyId: briefing.companyId,
        briefingType: briefing.briefingType,
        campaignName: briefing.campaignName ?? "",
        brandOrProduct: briefing.brandOrProduct ?? "",
        month: briefing.month,
        year: briefing.year,
        totalBudget: briefing.totalBudget ?? 0,
        fee: briefing.fee ?? 0,
        trackingPixel: briefing.trackingPixel,
        hasTags: briefing.hasTags,
        notes: briefing.notes ?? "",
        imageUrl: briefing.imageUrl ?? "",
      };
      setBase(loaded);
      setFormState(loaded);
    }
  }, [briefingResponse, id, navigate]);

  const companyOptions = useMemo(
    () =>
      companiesResponse?.data?.map((company) => ({
        id: company.id,
        name: company.nameCommercial || company.nameFiscal || company.id,
        logoUrl: company.logoUrl,
        feeVariable: company.feeVariable || 0,
      })) ?? [],
    [companiesResponse]
  );

  // A few years around today, always including the briefing's own year.
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const from = Math.min(current - 3, formState.year || current);
    const to = Math.max(current + 2, formState.year || current);
    return Array.from({ length: to - from + 1 }, (_, i) => String(from + i));
  }, [formState.year]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: longMonth(i + 1, i18n.language),
      })),
    [i18n.language]
  );

  // Automated Briefing Name Generation
  useEffect(() => {
    if (formState.month && formState.year && formState.briefingType) {
      const newName = buildBriefingName(formState);
      setFormState((prev) =>
        prev.name !== newName ? { ...prev, name: newName } : prev
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState.month,
    formState.year,
    formState.campaignName,
    formState.brandOrProduct,
    formState.briefingType,
  ]);

  if (!canManageBriefings) {
    return <Navigate to="/" replace />;
  }

  const dirtyFields = EDITABLE_FIELDS.filter(
    (field) => formState[field] !== base[field]
  );

  const fieldState = (field: keyof FormState): FieldState =>
    errors[field] ? "error" : dirtyFields.includes(field) ? "dirty" : "idle";

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    try {
      setIsUploadingImage(true);
      const url = await uploadBriefingImage(file, id);

      setFormState((prev) => ({ ...prev, imageUrl: url }));
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.imageUploadSuccess",
            "Image uploaded successfully"
          ),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Error uploading image:", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetImageUploadError",
            "Failed to upload image"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormState((prev) => ({ ...prev, imageUrl: "" }));
  };

  const handleChange = (
    field: keyof FormState,
    value: string | number | boolean
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCompanyChange = (selectedId: string) => {
    const selectedComp = companyOptions.find((c) => c.id === selectedId);
    setFormState((prev) => ({
      ...prev,
      companyId: selectedId,
      ...(selectedComp && { fee: selectedComp.feeVariable }),
    }));
    if (errors.companyId) {
      setErrors((prev) => ({ ...prev, companyId: undefined }));
    }
  };

  /** Undo of a single field: back to the saved value. */
  const revert = (field: keyof FormState) => () =>
    handleChange(field, base[field] as string | number | boolean);

  const handleDiscard = () => {
    setFormState(base);
    setErrors({});
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!formState.name.trim()) {
      nextErrors.name = t("briefingsCreate.errors.required");
    }
    if (!formState.companyId) {
      nextErrors.companyId = t("briefingsCreate.errors.required");
    }
    if (!formState.month || formState.month < 1 || formState.month > 12) {
      nextErrors.month = t("briefingsCreate.errors.invalidMonth");
    }
    if (!formState.year || formState.year < 2000) {
      nextErrors.year = t("briefingsCreate.errors.invalidYear");
    }
    if (!formState.totalBudget && formState.totalBudget !== 0) {
      nextErrors.totalBudget = t("briefingsCreate.errors.required");
    }
    if (!formState.fee && formState.fee !== 0) {
      nextErrors.fee = t("briefingsCreate.errors.required");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!id || !validate()) {
      return;
    }

    try {
      const payload: UpdateBriefingRequest = {
        name: formState.name.trim(),
        companyId: formState.companyId,
        briefingType: formState.briefingType,
        campaignName: formState.campaignName.trim() || undefined,
        brandOrProduct: formState.brandOrProduct.trim() || undefined,
        month: Number(formState.month),
        year: Number(formState.year),
        totalBudget: Number(formState.totalBudget),
        fee: Number(formState.fee),
        trackingPixel: formState.trackingPixel,
        hasTags: formState.hasTags,
        notes: formState.notes.trim() || undefined,
        imageUrl: formState.imageUrl || undefined,
      };

      await updateBriefing({ id, payload }).unwrap();

      dispatch(
        setToastMessage({
          message: t("briefingsEdit.toast.success", "Briefing updated"),
          severity: "success",
        })
      );
      navigate(`/briefings/${id}`);
    } catch (error: unknown) {
      console.error("Update briefing failed", error);
      dispatch(
        setToastMessage({
          message: t("briefingsEdit.toast.error", "Failed to update"),
          severity: "error",
        })
      );
    }
  };

  const handleCancel = () => {
    goBack(`/briefings/${id}`);
  };

  const briefingTypeLabel = (type: BriefingType) =>
    t(`briefingsCreate.form.briefingTypeOptions.${type}`);

  const yesNo = (value: boolean) =>
    value ? t("common.yes", "Sí") : t("common.no", "No");

  // Pieces of the generated name, in order, with the field each comes from.
  const nameParts = [
    {
      label: t("briefingsCreate.form.month"),
      value: formState.month.toString().padStart(2, "0"),
    },
    { label: t("briefingsCreate.form.year"), value: String(formState.year) },
    {
      label: t("briefingsCreate.form.briefingType"),
      value: formState.briefingType,
    },
    {
      label: t("briefingsCreate.form.brandOrProduct"),
      value: formState.brandOrProduct,
    },
    {
      label: t("briefingsCreate.form.campaignName"),
      value: formState.campaignName,
    },
  ];

  const nameHelper = (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {t(
          "briefingsEdit.nameHint",
          "Se genera automáticamente combinando estos campos:"
        )}
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        useFlexGap
        sx={{ mt: 0.75, rowGap: 0.75, columnGap: 0.5 }}
      >
        {nameParts.map((part, index) => (
          <React.Fragment key={part.label}>
            {index > 0 && (
              <Typography variant="caption" color="text.disabled">
                _
              </Typography>
            )}
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                opacity: part.value ? 1 : 0.5,
              }}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
              >
                {part.label}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={{ fontFamily: "monospace", lineHeight: 1.3 }}
              >
                {part.value || "—"}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleCancel}
              aria-label={t("briefingsCreate.back")}
              color="primary"
            >
              <KeyboardBackspaceIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {t("briefingsEdit.title", "Edit briefing")}
            </Typography>
          </Box>

          {companiesError && (
            <Alert severity="error">
              {t("briefingsCreate.errors.companyLoad")}
            </Alert>
          )}

          {briefingError && (
            <Alert severity="error">
              {t("briefingsEdit.errors.load", "Failed to load briefing")}
            </Alert>
          )}

          <Box sx={{ position: "relative" }}>
            {(isLoadingCompanies || isFetchingBriefing) && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 3,
                  zIndex: 1,
                }}
              >
                <OrbLoader size="medium" />
              </Box>
            )}

            <SectionCard
              icon={<DescriptionOutlinedIcon />}
              title={t("briefingsEdit.sectionTitle", "Datos del briefing")}
              subtitle={t(
                "briefingsEdit.sectionSubtitle",
                "Campaña, periodo, presupuesto e identificación"
              )}
              dirtyCount={dirtyFields.length}
              saving={isUpdating}
              onSave={() => void handleSave()}
              onDiscard={handleDiscard}
              createdAt={briefingResponse?.data?.createdAt}
              updatedAt={briefingResponse?.data?.updatedAt}
            >
              <Stack spacing={3.5}>
                <FieldGroup
                  title={t("briefingsEdit.groups.campaign", "Campaña")}
                  columns={2}
                  gap={2}
                >
                  <Field
                    label={t("briefingsCreate.form.campaignName")}
                    icon={<CampaignOutlinedIcon />}
                    state={fieldState("campaignName")}
                    onRevert={revert("campaignName")}
                  >
                    <PillInput
                      state={fieldState("campaignName")}
                      value={formState.campaignName}
                      onChange={(event) =>
                        handleChange("campaignName", event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.brandOrProduct")}
                    icon={<StorefrontOutlinedIcon />}
                    state={fieldState("brandOrProduct")}
                    onRevert={revert("brandOrProduct")}
                  >
                    <PillInput
                      state={fieldState("brandOrProduct")}
                      value={formState.brandOrProduct}
                      onChange={(event) =>
                        handleChange("brandOrProduct", event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.company")}
                    icon={<BusinessOutlinedIcon />}
                    state={fieldState("companyId")}
                    error={errors.companyId}
                    onRevert={() => handleCompanyChange(base.companyId)}
                  >
                    <PillInput
                      select
                      state={fieldState("companyId")}
                      value={formState.companyId}
                      onChange={(event) =>
                        handleCompanyChange(event.target.value)
                      }
                      error={Boolean(errors.companyId)}
                    >
                      {companyOptions.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            minWidth={0}
                          >
                            <CompanyLogo
                              name={company.name}
                              logoUrl={company.logoUrl}
                            />
                            <Typography variant="body1" noWrap>
                              {company.name}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </PillInput>
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.briefingType")}
                    icon={<CategoryOutlinedIcon />}
                    state={fieldState("briefingType")}
                    onRevert={revert("briefingType")}
                  >
                    <ControlRow>
                      <RadioGroup
                        row
                        value={formState.briefingType}
                        onChange={(event) =>
                          handleChange(
                            "briefingType",
                            event.target.value as BriefingType
                          )
                        }
                        sx={{ columnGap: 2 }}
                      >
                        {BRIEFING_TYPES.map((type) => (
                          <FormControlLabel
                            key={type}
                            value={type}
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                {briefingTypeLabel(type)}
                              </Typography>
                            }
                            sx={{ mr: 0 }}
                          />
                        ))}
                      </RadioGroup>
                    </ControlRow>
                  </Field>
                </FieldGroup>

                <FieldGroup
                  title={t(
                    "briefingsEdit.groups.periodBudget",
                    "Periodo y presupuesto"
                  )}
                  columns={4}
                  gap={2}
                >
                  <Field
                    label={t("briefingsCreate.form.month")}
                    icon={<CalendarMonthOutlinedIcon />}
                    state={fieldState("month")}
                    error={errors.month}
                    onRevert={revert("month")}
                  >
                    <PillInput
                      select
                      state={fieldState("month")}
                      value={String(formState.month)}
                      onChange={(event) =>
                        handleChange("month", Number(event.target.value))
                      }
                      error={Boolean(errors.month)}
                    >
                      {monthOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </PillInput>
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.year")}
                    icon={<EventOutlinedIcon />}
                    state={fieldState("year")}
                    error={errors.year}
                    onRevert={revert("year")}
                  >
                    <PillInput
                      select
                      state={fieldState("year")}
                      value={String(formState.year)}
                      onChange={(event) =>
                        handleChange("year", Number(event.target.value))
                      }
                      error={Boolean(errors.year)}
                    >
                      {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </PillInput>
                  </Field>
                  <Field
                    label={t(
                      "briefingsCreate.form.totalBudget",
                      "Total budget"
                    )}
                    icon={<EuroOutlinedIcon />}
                    state={fieldState("totalBudget")}
                    error={errors.totalBudget}
                    onRevert={revert("totalBudget")}
                  >
                    <PillInput
                      type="number"
                      state={fieldState("totalBudget")}
                      value={formState.totalBudget}
                      onChange={(event) =>
                        handleChange("totalBudget", Number(event.target.value))
                      }
                      error={Boolean(errors.totalBudget)}
                      inputProps={{ step: "any" }}
                    />
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.fee", "Fee")}
                    icon={<PercentOutlinedIcon />}
                    state={fieldState("fee")}
                    error={errors.fee}
                    onRevert={revert("fee")}
                  >
                    <PillInput
                      type="number"
                      state={fieldState("fee")}
                      value={formState.fee}
                      onChange={(event) =>
                        handleChange("fee", Number(event.target.value))
                      }
                      error={Boolean(errors.fee)}
                      inputProps={{ step: "any" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  </Field>
                </FieldGroup>

                <FieldGroup
                  title={t(
                    "briefingsView.groups.identification",
                    "Identificación"
                  )}
                  columns={1}
                  gap={2}
                >
                  <Field
                    label={t("briefingsCreate.form.name")}
                    icon={<LabelOutlinedIcon />}
                    auto
                    autoHint={t(
                      "briefingsEdit.nameFormula",
                      "Mes_Año_Tipo de briefing_Marca o producto_Nombre de la campaña"
                    )}
                    state={errors.name ? "error" : "idle"}
                    error={errors.name}
                    helper={nameHelper}
                  >
                    <PillInput
                      auto
                      state={errors.name ? "error" : "idle"}
                      value={formState.name}
                      error={Boolean(errors.name)}
                      // Hug the text instead of stretching across the page.
                      // The font is monospace, so ch matches the text width;
                      // longer names than the row still shrink and scroll.
                      fullWidth={false}
                      sx={{ maxWidth: "100%" }}
                      inputProps={{
                        style: {
                          width: `${Math.max(formState.name.length, 20) + 1}ch`,
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: "0.9rem" },
                        endAdornment: formState.name ? (
                          <InputAdornment position="end">
                            <Tooltip title={t("common.copy", "Copy")}>
                              <IconButton
                                size="small"
                                edge="end"
                                onClick={() => copy(formState.name)}
                                aria-label={t("common.copy", "Copy")}
                              >
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Field>
                </FieldGroup>

                <FieldGroup
                  title={t(
                    "briefingsView.groups.notesAndFiles",
                    "Notas y adjuntos"
                  )}
                  columns={2}
                  gap={2}
                >
                  <Field
                    span="full"
                    label={t("briefingsCreate.form.notes")}
                    icon={<NotesOutlinedIcon />}
                    state={fieldState("notes")}
                    onRevert={revert("notes")}
                  >
                    <PillInput
                      multiline
                      minRows={2}
                      state={fieldState("notes")}
                      value={formState.notes}
                      onChange={(event) =>
                        handleChange("notes", event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.trackingPixel")}
                    icon={<GpsFixedOutlinedIcon />}
                    state={fieldState("trackingPixel")}
                    onRevert={revert("trackingPixel")}
                  >
                    <ControlRow>
                      <Switch
                        size="small"
                        checked={formState.trackingPixel}
                        onChange={(event) =>
                          handleChange("trackingPixel", event.target.checked)
                        }
                      />
                      <Typography variant="body1">
                        {yesNo(formState.trackingPixel)}
                      </Typography>
                    </ControlRow>
                  </Field>
                  <Field
                    label={t("briefingsCreate.form.hasTags")}
                    icon={<SellOutlinedIcon />}
                    state={fieldState("hasTags")}
                    onRevert={revert("hasTags")}
                  >
                    <ControlRow>
                      <Switch
                        size="small"
                        checked={formState.hasTags}
                        onChange={(event) =>
                          handleChange("hasTags", event.target.checked)
                        }
                      />
                      <Typography variant="body1">
                        {yesNo(formState.hasTags)}
                      </Typography>
                    </ControlRow>
                  </Field>
                  <Field
                    span="full"
                    label={t("briefingsCreate.form.image", "Briefing image")}
                    icon={<ImageOutlinedIcon />}
                    state={fieldState("imageUrl")}
                    onRevert={revert("imageUrl")}
                  >
                    <Box sx={{ px: 1 }}>
                      {formState.imageUrl ? (
                        <Box
                          sx={{
                            position: "relative",
                            display: "inline-block",
                            mt: 0.5,
                          }}
                        >
                          <img
                            src={formState.imageUrl}
                            alt={t(
                              "briefingsCreate.form.image",
                              "Briefing image"
                            )}
                            style={{
                              height: 100,
                              width: "auto",
                              borderRadius: 12,
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={handleRemoveImage}
                            aria-label={t("common.delete", "Delete")}
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              bgcolor: "background.paper",
                              boxShadow: 1,
                              "&:hover": { bgcolor: "background.paper" },
                            }}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={
                            isUploadingImage ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CloudUploadIcon />
                            )
                          }
                          disabled={isUploadingImage}
                          sx={{ borderRadius: 50 }}
                        >
                          {isUploadingImage
                            ? t("common.uploading", "Uploading...")
                            : t("common.uploadImage", "Upload Image")}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageUpload}
                          />
                        </Button>
                      )}
                    </Box>
                  </Field>
                </FieldGroup>
              </Stack>
            </SectionCard>
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default EditBriefing;
